import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styles from './LLM.module.css';
import { Centered } from '../layouts/Base';
import { AGENT_SETUP_BASE } from '../constants/setupRoadmapSteps';
import AgentSelector from '../components/AgentSelector';
import ConversationHistory from '../components/agents/ConversationHistory';
import api from '../API.js';
import { useAuth } from '../providers/AuthContext';

function LLM() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const isAdmin = user && user.admin;
    
    const [models, setModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState('');
    const [modelOverride, setModelOverride] = useState(false);
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [prompt, setPrompt] = useState('');
    const [response, setResponse] = useState('');
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedCollection, setSelectedCollection] = useState('');
    const [conversationHistory, setConversationHistory] = useState([]);
    const [historyCollapsed, setHistoryCollapsed] = useState(true);
    const [saveConversation, setSaveConversation] = useState(false);
    const [conversationId, setConversationId] = useState(null);

    // Check for agent parameter in URL
    useEffect(() => {
        const agentParam = searchParams.get('agent');
        if (agentParam && agentParam !== selectedAgentId) {
            setSelectedAgentId(agentParam);
        }
    }, [searchParams, selectedAgentId]);


    // Fetch available models on component mount
    useEffect(() => {
        fetch('/llmapi/api/tags')
            .then(res => res.json())
            .then(data => {
                if (data.models && data.models.length > 0) {
                    const modelNames = data.models.map(m => m.name);
                    setModels(modelNames);
                }
            })
            .catch(err => {
                console.error('Error fetching models:', err);
                // Don't set error for model fetch - RAG will work without it
            });
    }, []);

    // Fetch agent details when agentId changes
    useEffect(() => {
        if (selectedAgentId) {
            api.get(`agent/${selectedAgentId}`)
                .then(response => {
                    if (response.status === 200) {
                        const data = response.data;
                        setSelectedAgent(data);
                        // Set agent's default model if available and not overriding
                        if (data.defaultModel && !modelOverride) {
                            setSelectedModel(data.defaultModel);
                        }
                        // Set first collection as default if available
                        if (data.rag_collections && data.rag_collections.length > 0) {
                            setSelectedCollection(data.rag_collections[0]);
                        } else {
                            setSelectedCollection('');
                        }
                    } else {
                        throw new Error(`Failed to fetch agent: ${response.status}`);
                    }
                })
                .catch(err => {
                    console.error('Error fetching agent details:', err);
                    setSelectedAgent(null);
                });
        } else {
            setSelectedAgent(null);
            setSelectedCollection('');
            if (!modelOverride) {
                setSelectedModel('');
            }
        }
    }, [selectedAgentId]);
    
    // Reset model to agent default when override is turned off
    useEffect(() => {
        if (!modelOverride && selectedAgent?.defaultModel) {
            setSelectedModel(selectedAgent.defaultModel);
        }
    }, [modelOverride, selectedAgent]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!prompt.trim()) {
            setError('Please enter a prompt.');
            return;
        }

        if (!selectedAgentId) {
            setError('Please select an agent first.');
            return;
        }

        setLoading(true);
        setError('');
        setResponse('');
        setSources([]);

        try {
            const formData = new FormData();
            formData.append('query', prompt);
            // Use agent's default model if no override is set, otherwise use selectedModel
            const modelToUse = modelOverride && selectedModel ? selectedModel : (selectedAgent?.defaultModel || selectedModel);
            if (modelToUse) {
                formData.append('model', modelToUse);
            }
            formData.append('topK', '5');
            formData.append('minSimilarity', '0.7');
            
            // Test mode: use in-memory conversation history
            formData.append('testMode', 'true');
            
            // If saveConversation is true and we have a conversationId, use it
            // Otherwise, if saveConversation is true, create a new conversation first
            let currentConversationId = conversationId;
            if (saveConversation && !currentConversationId) {
                // Create new conversation
                const convResponse = await api.post('conversation', {
                    body: {
                        agentId: selectedAgentId,
                        isAdminTraining: false,
                    },
                });
                if (convResponse.status === 200 && convResponse.data.success) {
                    currentConversationId = convResponse.data.conversationId;
                    setConversationId(currentConversationId);
                }
            }
            
            // Send conversation history if in test mode (or if we want to use edited history)
            if (conversationHistory.length > 0) {
                formData.append('conversationHistory', JSON.stringify(conversationHistory));
            }
            
            // If saving conversation, also send conversationId
            if (saveConversation && currentConversationId) {
                formData.append('conversationId', currentConversationId);
            }

            const response = await api.post(`agent/${selectedAgentId}/rag/query`, {
                body: formData,
            });

            if (response.status === 200 && response.data.success) {
                const responseText = response.data.response || 'No response generated.';
                setResponse(responseText);
                setSources(response.data.sources || []);
                
                // Update conversation history if returned
                if (response.data.conversationHistory) {
                    setConversationHistory(response.data.conversationHistory);
                } else {
                    // Fallback: manually add to history
                    const updatedHistory = [...conversationHistory];
                    updatedHistory.push({
                        role: 'user',
                        content: prompt,
                        timestamp: new Date().toISOString(),
                    });
                    updatedHistory.push({
                        role: 'assistant',
                        content: responseText,
                        timestamp: new Date().toISOString(),
                        metadata: {
                            sources: response.data.sources || [],
                        },
                    });
                    setConversationHistory(updatedHistory);
                }
            } else {
                // Extract error message from response
                const errorData = response.data || {};
                const errorCode = errorData.code || 'UNKNOWN_ERROR';
                const userMessage = errorData.userMessage || errorData.message || 'Failed to generate response';
                throw new Error(`${errorCode}: ${userMessage}`);
            }
        } catch (err) {
            console.error('Error generating response:', err);
            
            // Extract error code and message if available
            let errorMessage = 'Failed to generate response. ';
            if (err.message) {
                // Check if error message contains error code
                if (err.message.includes(':')) {
                    const parts = err.message.split(':');
                    errorMessage = parts.length > 1 ? parts.slice(1).join(':').trim() : err.message;
                } else {
                    errorMessage += err.message;
                }
            } else {
                errorMessage += 'Please try again.';
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };


    const handleClear = () => {
        setPrompt('');
        setResponse('');
        setSources([]);
        setError('');
    };

    const handleClearHistory = () => {
        if (window.confirm('Are you sure you want to clear the conversation history?')) {
            setConversationHistory([]);
            setConversationId(null);
        }
    };

    const handleHistoryUpdate = (updatedHistory) => {
        setConversationHistory(updatedHistory);
    };

    return (
        <Centered>
            <div className={styles.LLM}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h1 style={{ margin: 0 }}>RAG-Enhanced LLM Chat Interface</h1>
                    {isAdmin && (
                        <Link 
                            to={`${AGENT_SETUP_BASE}/manage`} 
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                textDecoration: 'none',
                                borderRadius: '5px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                            }}
                        >
                            Manage Agents
                        </Link>
                    )}
                </div>
                
                {/* Conversation History */}
                <ConversationHistory
                    messages={conversationHistory}
                    onUpdate={handleHistoryUpdate}
                    onClear={handleClearHistory}
                    isCollapsed={historyCollapsed}
                    onToggleCollapse={() => setHistoryCollapsed(!historyCollapsed)}
                />

                {/* Agent Selection */}
                <div className={styles.agentSection} style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                    <AgentSelector
                        selectedAgentId={selectedAgentId}
                        onAgentChange={setSelectedAgentId}
                    />
                    {selectedAgent && (
                        <div style={{ marginTop: '10px', fontSize: '14px' }}>
                            <div><strong>Collections:</strong> {selectedAgent.rag_collections?.length || 0}</div>
                            <div style={{ marginTop: '5px' }}>
                                <strong>Model:</strong> {modelOverride && selectedModel ? selectedModel : (selectedAgent.defaultModel || 'default')}
                                {!modelOverride && selectedAgent.defaultModel && (
                                    <span style={{ color: '#666', fontSize: '12px', marginLeft: '5px' }}>
                                        (agent default)
                                    </span>
                                )}
                                {modelOverride && (
                                    <span style={{ color: '#666', fontSize: '12px', marginLeft: '5px' }}>
                                        (override)
                                    </span>
                                )}
                            </div>
                            {selectedAgent.rag_collections && selectedAgent.rag_collections.length > 0 && (
                                <div style={{ marginTop: '5px' }}>
                                    <label htmlFor="collection-select" style={{ marginRight: '10px' }}>
                                        <strong>Target Collection:</strong>
                                    </label>
                                    <select
                                        id="collection-select"
                                        value={selectedCollection}
                                        onChange={(e) => setSelectedCollection(e.target.value)}
                                        style={{ padding: '5px 10px', fontSize: '14px' }}
                                    >
                                        {selectedAgent.rag_collections.map(coll => (
                                            <option key={coll} value={coll}>{coll}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <Link
                                    to={`${AGENT_SETUP_BASE}/collections`}
                                    style={{
                                        padding: '5px 10px',
                                        fontSize: '12px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        textDecoration: 'none',
                                        borderRadius: '3px',
                                    }}
                                >
                                    Manage Collections →
                                </Link>
                                {selectedCollection && (
                                    <Link
                                        to={`${AGENT_SETUP_BASE}/documents?collection=${encodeURIComponent(selectedCollection)}`}
                                        style={{
                                            padding: '5px 10px',
                                            fontSize: '12px',
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            textDecoration: 'none',
                                            borderRadius: '3px',
                                        }}
                                    >
                                        View Documents →
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Query Section */}
                <div className={styles.querySection}>
                    <h2>Ask a Question</h2>
                    {error && (
                        <div className={styles.error} style={{
                            padding: '12px',
                            backgroundColor: '#f8d7da',
                            border: '1px solid #f5c6cb',
                            borderRadius: '4px',
                            color: '#721c24',
                            marginBottom: '15px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span>{error}</span>
                            <button
                                onClick={() => setError('')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                    color: '#721c24',
                                    padding: '0 5px'
                                }}
                                title="Dismiss error"
                            >
                                ×
                            </button>
                        </div>
                    )}
                    
                    {loading && (
                        <div style={{
                            padding: '12px',
                            backgroundColor: '#d1ecf1',
                            border: '1px solid #bee5eb',
                            borderRadius: '4px',
                            color: '#0c5460',
                            marginBottom: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid #0c5460',
                                borderTop: '2px solid transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            <span>Processing your request...</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                <label htmlFor="model">Model:</label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setModelOverride(!modelOverride);
                                        if (!modelOverride && selectedAgent?.defaultModel) {
                                            setSelectedModel(selectedAgent.defaultModel);
                                        }
                                    }}
                                    style={{
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        backgroundColor: modelOverride ? '#28a745' : '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {modelOverride ? 'Using Override' : 'Advanced: Override Model'}
                                </button>
                            </div>
                            {modelOverride ? (
                                <select
                                    id="model"
                                    value={selectedModel}
                                    onChange={(e) => setSelectedModel(e.target.value)}
                                    disabled={loading || models.length === 0}
                                    className={styles.select}
                                >
                                    {models.length === 0 ? (
                                        <option>Loading models...</option>
                                    ) : (
                                        <>
                                            <option value="">Use agent default</option>
                                            {models.map(model => (
                                                <option key={model} value={model}>
                                                    {model}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            ) : (
                                <div style={{ padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '3px', fontSize: '14px', color: '#666' }}>
                                    {selectedAgent?.defaultModel || 'No model selected'}
                                    <span style={{ fontSize: '12px', marginLeft: '5px' }}>(agent default)</span>
                                </div>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="prompt">Question:</label>
                            <textarea
                                id="prompt"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                disabled={loading}
                                placeholder="Enter your question here..."
                                className={styles.textarea}
                                rows={6}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={saveConversation}
                                    onChange={(e) => setSaveConversation(e.target.checked)}
                                    disabled={loading}
                                />
                                <span>Save conversation to database (for longitudinal testing)</span>
                            </label>
                        </div>

                        <div className={styles.buttonGroup}>
                            <button
                                type="submit"
                                disabled={loading || !prompt.trim()}
                                className={styles.submitButton}
                            >
                                {loading ? 'Generating...' : 'Ask Question'}
                            </button>
                            <button
                                type="button"
                                onClick={handleClear}
                                disabled={loading}
                                className={styles.clearButton}
                            >
                                Clear
                            </button>
                        </div>
                    </form>
                </div>

                {/* Response Section */}
                {response && (
                    <div className={styles.response}>
                        <h3>Response:</h3>
                        <div className={styles.responseText}>
                            {response}
                        </div>
                    </div>
                )}

                {/* Sources Section */}
                {sources && sources.length > 0 && (
                    <div className={styles.sources}>
                        <h3>Sources ({sources.length}):</h3>
                        <div className={styles.sourcesList}>
                            {sources.map((source, idx) => (
                                <div key={idx} className={styles.sourceCard}>
                                    <div className={styles.sourceHeader}>
                                        <span className={styles.sourceTitle}>{source.title}</span>
                                        <span className={styles.sourceType}>{source.sourceType}</span>
                                        <span className={styles.similarity}>
                                            {(source.similarity * 100).toFixed(1)}% match
                                        </span>
                                    </div>
                                    <div className={styles.sourceContent}>
                                        {source.content}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Centered>
    );
}

export default LLM;
